import { useEffect, useMemo } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PortalTopNav } from '@/components/portal/PortalTopNav';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Helmet } from '@/components/Helmet';
import { NAV_LABELS } from '@/lib/vocabulary/securityTerminology';
import { formatTimestampSafe } from '@/lib/utils/formatTimestamp';

const formatTimestamp = (value?: string | null) => (value ? formatTimestampSafe(value) : '—');

export default function DomainLayout() {
  const { domainId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { isImpersonating, activeCustomerId, activeCustomerEmail, activeCustomerName, clearImpersonation } = useImpersonation();
  const { checking } = useRequireMfa();
  const isImpersonationView = Boolean(isImpersonating && activeCustomerId);

  // Check if user is admin (admins can view any domain via RLS)
  const { data: isAdmin } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user && !checking,
  });

  const { data: domain, isPending: domainPending } = useQuery({
    queryKey: ['domain', domainId, isAdmin, isImpersonationView, activeCustomerId],
    queryFn: async () => {
      let query = supabase
        .from('domains')
        .select('id, domain_name, status, created_at, stripe_price_id, tier, vuln_enrichment_enabled')
        .eq('id', domainId!);

      // Keep impersonation scoped to the selected customer even for admins.
      if (isImpersonationView && activeCustomerId) {
        query = query.eq('user_id', activeCustomerId);
      } else if (!isAdmin) {
        query = query.eq('user_id', user!.id);
      }

      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!domainId && !!user && isAdmin !== undefined && (!isImpersonationView || !!activeCustomerId),
  });

  const { data: managedSite } = useQuery({
    queryKey: ['managed-site', domainId, isAdmin, isImpersonationView, activeCustomerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('managed_sites')
        .select('id, last_heartbeat_at, site_url, wp_admin_url')
        .eq('domain_id', domainId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!domainId && !!user && isAdmin !== undefined,
  });

  const isAdminResolved = isAdmin !== undefined;

  useEffect(() => {
    if (!checking && !loading && user && isAdminResolved && !domainPending && !domain) {
      if (isImpersonationView && activeCustomerId) {
        navigate(`/portal/admin/impersonate/${activeCustomerId}`, { replace: true });
      } else {
        navigate('/portal', { replace: true });
      }
    }
  }, [checking, loading, user, isAdminResolved, domainPending, domain, navigate, isImpersonationView, activeCustomerId]);

  if (checking || loading || !isAdminResolved || domainPending) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading...</div>
      </div>
    );
  }

  if (!domain) {
    return null;
  }

  const tabs = [
    { label: 'Overview', to: `/portal/domains/${domain.id}` },
    { label: NAV_LABELS.protectionCoverage, to: `/portal/domains/${domain.id}/coverage` },
    { label: NAV_LABELS.adminAccessRules, to: `/portal/domains/${domain.id}/access-policy` },
    { label: NAV_LABELS.securityActivity, to: `/portal/domains/${domain.id}/logs` },
    { label: NAV_LABELS.evidenceExport, to: `/portal/domains/${domain.id}/evidence` },
    { label: 'Settings', to: `/portal/domains/${domain.id}/settings` },
  ];
  const impersonationHomePath = isImpersonationView && activeCustomerId
    ? `/portal/admin/impersonate/${activeCustomerId}`
    : '/portal';

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>{domain.domain_name} | ShadowScan Portal</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <PortalTopNav
        userEmail={user?.email || ''}
        userName={user?.user_metadata?.full_name || undefined}
        context={isImpersonationView ? 'admin_impersonation' : undefined}
        homePath={impersonationHomePath}
        onExitImpersonation={isImpersonationView ? () => {
          clearImpersonation();
          navigate('/portal/admin');
        } : undefined}
        active="dashboard"
      />

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-6xl space-y-6">
        <Card className="border-border/60">
          <CardContent className="flex flex-col gap-4 py-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-heading font-semibold">{domain.domain_name}</h1>
                  <Badge variant="outline">Domain</Badge>
                  {isImpersonationView && (
                    <Badge variant="warning">
                      Customer view: {activeCustomerName || activeCustomerEmail || activeCustomerId}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Added {new Date(domain.created_at).toLocaleDateString()}</p>
                <p className="text-xs text-muted-foreground">Last check-in: {formatTimestamp(managedSite?.last_heartbeat_at)}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(impersonationHomePath)}>
                Back to Dashboard
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <Button
                  key={tab.to}
                  asChild
                  variant={location.pathname === tab.to ? 'secondary' : 'outline'}
                  size="sm"
                >
                  <NavLink to={tab.to}>{tab.label}</NavLink>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Outlet context={{ domain, managedSite }} />
      </main>
    </div>
  );
}
